import { useState, Fragment, useEffect } from "react";
import landingStyles from './landing.module.css';
import { LANGUAGE_NAMES } from "../../constants";
import kanjiLists from "../../kanjiLists";
import LANDING_EXAMPLE_DE from './LandingExampleDE.json';
import LANDING_EXAMPLE_EN from './LandingExampleEN.json';
import LANDING_EXAMPLE_ES from './LandingExampleES.json';
import LANDING_EXAMPLE_FR from './LandingExampleFR.json';
import LANDING_EXAMPLE_HI from './LandingExampleHI.json';
import LANDING_EXAMPLE_JA from './LandingExampleJA.json';


const EXAMPLES = {
  'en': LANDING_EXAMPLE_EN,
  'es': LANDING_EXAMPLE_ES,
  'de': LANDING_EXAMPLE_DE,
  'fr': LANDING_EXAMPLE_FR,
  'hi': LANDING_EXAMPLE_HI,
  'ja': LANDING_EXAMPLE_JA,
}

// paragraph component, shows text of paragraph and explanation block below if sentence within paragraph clicked
const Paragraph = ({ paragraph, selectedSentence, setSelectedSentence, lang }) => {
  return (
    <div>
      {/* text block */}
      <p>
        {paragraph.sentences.map((sentence, index) => (
          <Fragment key={index}>
            <Sentence
              sentence={sentence}
              isSelected={selectedSentence === sentence}
              setSelectedSentence={setSelectedSentence}
              lang={lang}
            />
            {index < paragraph.sentences.length - 1 && ' '}
          </Fragment>
        ))}
      </p>
    </div>
  );
};


function ExplanationBlock({sentence, textLanguage, userLanguage}) {

  const translation = sentence.translations[userLanguage];
  const breakdown = sentence.breakdown[userLanguage];
  const notes = [...sentence.grammar_notes[userLanguage], ...sentence.idiom_notes[userLanguage]]
  const transliteration = sentence.transliteration;
  const kana = sentence.kana;

  return (
    <div className={landingStyles.explanationBlock}>
      {
        textLanguage === 'hi' && (
          <>
            <p><strong>Pronunciation</strong></p>
            <p lang={textLanguage} translate="no" className="notranslate">{transliteration}</p>
            <br/>
          </>
        )
      }
      {
        textLanguage === 'ja' && (
          <>
            <p><strong>Kana</strong></p>
            <p lang={textLanguage} translate="no" className="notranslate">{kana}</p>
            <br/>
          </>
        )
      }
      <p><strong>Translation:</strong></p>
      <p lang={userLanguage} translate="no" className="notranslate">{translation}</p>
      <br/>
      <p><strong>Breakdown:</strong></p>
      <ul>
        {breakdown.map((item, index) => (
          <li key={index}>
            <strong lang={textLanguage} translate="no" className="notranslate">{item.text}</strong>: ({textLanguage === 'ja' && item.kana !== item.text && <span lang={textLanguage} translate="no" className="notranslate">{`${item.kana}; `}</span>}<span lang={userLanguage} translate="no" className="notranslate">{item.category}</span>) <span lang={userLanguage} translate="no" className="notranslate">{item.meaning}</span>
          </li>
        ))}
      </ul>
      <br/>
      {
        notes.length !== 0 && (
          <>
            <p><strong>Notes</strong></p>
            <ul>
              {notes.map((item, index) => <li key={index} lang={userLanguage} translate="no" className="notranslate">{item}</li>)}
            </ul>
            <br/>
          </>
        )
      }
    </div>
  )
} 

// sentence component, shows text of sentence
const Sentence = ({ sentence, isSelected, setSelectedSentence, lang }) => {

  const onClick = () => {
    if (!isSelected) {
      setSelectedSentence(sentence);
    }
  }

  let className = `notranslate ${landingStyles.sentence}`;
  if (isSelected) {
    className += ` ${landingStyles.sentenceSelected}`
  } else {
    className += ` ${landingStyles.sentenceClickable}`;
  }

  const renderJapaneseText = () => {
    const result = [];
    sentence.kakasi.forEach((token, ind) => {
      // if token is pure katakana or hiragana or punctuation, return original
      if((token.orig === token.kata) || (token.orig === token.hira)) {
        result.push(<span className={`notranslate ${className}`} key={ind}>{token.orig}</span>)
      }
      // else, check to see if kanji all easy, otherwise set up furigana 
      else {
        const kanjiMatches = token.orig.match(/[\p{Script=Han}]/gu) || [];
        const allEasy = kanjiMatches.every(kanji => kanjiLists['beg'].has(kanji));
        if (allEasy) {
          result.push(<span key={ind} className={`notranslate ${className}`}>{token.orig}</span>)
        } else {
          result.push(
            <ruby key={ind} className={`notranslate ${className}`}>
              {token.orig}
              <rt>{token.hira}</rt>
            </ruby>
          )
        }
      }
    })
    return result;
  }

  return (
    <span lang={lang} translate="no" onClick={onClick} className={className}>
      {lang !== 'ja' ? sentence.text : renderJapaneseText()}
    </span>
  );
};


function LandingDemo() {

  const [selectedReadingLanguage, setSelectedReadingLanguage] = useState('fr');
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] = useState(EXAMPLES[selectedReadingLanguage].translation_languages[0]);
  const [selectedSentence, setSelectedSentence] = useState(EXAMPLES[selectedReadingLanguage].paragraphs[0].sentences[0]);

  useEffect(() => {
    setSelectedTranslationLanguage(EXAMPLES[selectedReadingLanguage].translation_languages[0])
    setSelectedSentence(EXAMPLES[selectedReadingLanguage].paragraphs[0].sentences[0])
  }, [selectedReadingLanguage])

  return (
    <div className={landingStyles.exampleContainer}>
      <h2>Demo</h2>
      <p className={landingStyles.demoInstructions}>Story beginnings in various languages, all at 'Beginner' level. Click on sentences to see translations and explanations.</p>
      <p>For full stories or 'Intermediate'/'Advanced' levels, please sign up!</p>
      <div className={landingStyles.dropdownSection}>
        <div className={landingStyles.dropdownContainer}>
          <p>Language</p>
          <select
            name="readingLanguage"
            value={selectedReadingLanguage}
            onChange={(e) => setSelectedReadingLanguage(e.target.value)}
            className={landingStyles.dropdown}
          >
            {['fr', 'de', 'hi', 'ja', 'en', 'es'].map((lang) => (
              <option key={lang} value={lang}>
                {LANGUAGE_NAMES[lang]}
              </option>
            ))}
          </select>
        </div>
        <div className={landingStyles.dropdownContainer}>
        <p>Translation</p>
          <select
            name="translationLanguage"
            value={selectedTranslationLanguage}
            onChange={(e) => setSelectedTranslationLanguage(e.target.value)}
            className={landingStyles.dropdown}
          >
            {EXAMPLES[selectedReadingLanguage].translation_languages.map((lang) => (
              <option key={lang} value={lang}>
                {LANGUAGE_NAMES[lang]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <hr className={landingStyles.hrule} />
      {EXAMPLES[selectedReadingLanguage].paragraphs.map((paragraph, index) => {
        return (
          <Paragraph
            key={index}
            paragraph={paragraph}
            selectedSentence={selectedSentence}
            setSelectedSentence={setSelectedSentence}
            lang={selectedReadingLanguage}
          />
        )
      })}
      <ExplanationBlock sentence={selectedSentence} textLanguage={selectedReadingLanguage} userLanguage={selectedTranslationLanguage}/>
      <div className={landingStyles.disclaimerContainer}>
        <p className={landingStyles.disclaimer}>Example preview. Features and designs may change in the live version of the app.</p>
      </div>
    </div>
  )
}

export default LandingDemo